/* DPM: instant header cart-count feedback on Add to Bag — see the
   onSubmitHandler call sites below. The visible count lives in
   #cart-icon-bubble .dpm-pill-btn__count (sections/cart-icon-bubble.liquid),
   which Liquid only renders at all once cart.item_count > 0 — so this
   creates that span the first time an item is added to an empty cart,
   rather than assuming it already exists. */
function bumpOptimisticCartCount(delta) {
  const bubble = document.getElementById('cart-icon-bubble');
  const label = bubble && bubble.querySelector('.dpm-pill-btn__label');
  if (!label) return;

  let countEl = label.querySelector('.dpm-pill-btn__count');
  if (countEl) {
    const current = parseInt(countEl.textContent, 10) || 0;
    countEl.textContent = current + delta;
  } else {
    countEl = document.createElement('span');
    countEl.className = 'dpm-pill-btn__count';
    countEl.textContent = delta;
    label.appendChild(countEl);
  }
}

function revertOptimisticCartCount(delta) {
  const bubble = document.getElementById('cart-icon-bubble');
  const countEl = bubble && bubble.querySelector('.dpm-pill-btn__count');
  if (!countEl) return;

  const reverted = (parseInt(countEl.textContent, 10) || 0) - delta;
  if (reverted > 0) {
    countEl.textContent = reverted;
  } else {
    countEl.remove();
  }
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        /* DPM: submit start time, used below to keep the quick-add icon's
           loading -> checkmark -> drawer-open sequence correctly paced
           regardless of how fast /cart/add.js actually responds. */
        const submitStartedAt = Date.now();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        /* DPM: bump the header cart icon's count the instant Add to Bag is
           pressed, rather than waiting on the full /cart/add.js round trip
           — the count is corrected either way once the real response comes
           back (renderContents() overwrites it on success; revertOptimisticCartCount()
           below undoes exactly this bump on error/sold-out). */
        const addedQuantity = parseInt(formData.get('quantity'), 10) || 1;
        bumpOptimisticCartCount(addedQuantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              revertOptimisticCartCount(addedQuantity);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              const paintCart = () => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              };
              if (this.submitButton.classList.contains('quick-add__icon-button')) {
                /* DPM: the + shows a spinner (dopamean-quick-add-feedback.js,
                   driven by --pending in dopamean-quick-add-icon.css) for the
                   whole wait, right up until the drawer opens — no checkmark
                   stage in between. Hold for a guaranteed minimum stretch so
                   a very fast /cart/add.js response doesn't make the spinner
                   flash by unnoticed, then fire an explicit event right as
                   the drawer is about to open so that script can drop the
                   spinner back to + at exactly that moment, instead of the
                   two scripts having to keep matching timing constants in
                   sync. */
                const MIN_LOADING_MS = 300;
                const elapsed = Date.now() - submitStartedAt;
                const openDelay = Math.max(0, MIN_LOADING_MS - elapsed);
                setTimeout(() => {
                  document.dispatchEvent(
                    new CustomEvent('dpm:quick-add:cart-opening', { detail: { variantId: formData.get('id') } })
                  );
                  paintCart();
                }, openDelay);
              } else {
                paintCart();
              }
            }
          })
          .catch((e) => {
            revertOptimisticCartCount(addedQuantity);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          /* DPM: gift card products use a custom CTA. The Liquid-side
             gift-card check in buy-buttons.liquid sets it on initial render,
             but this method runs on every variant change and would otherwise
             clobber it back to the generic add-to-cart locale string. */
          const isGiftCard = document.body.classList.contains('template-product-gift-card');
          this.submitButtonText.textContent = isGiftCard
            ? 'GET ARCHIVE ACCESS'
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
